/**
 * Central Definitions File
 *
 * All flipcard and info-banner definitions in one place.
 * Edit this file to update definitions across the entire application.
 *
 * Placeholders use {key} syntax and are replaced dynamically:
 * - {anode}, {cathode}, {chemistry}, {ionLabel} - Chemical data
 * - {housing}, {format} - Mechanical data
 * - {I_c10}, {V_min}, {V_max}, {T_max} - Electrical/Thermal data
 * - {EFC_initial_aging} - Aging data
 * - {thermalBoundary} - "braced" or "free convection"
 * - {IDS_electrical_C_rate_DCP} - Discharge pulse C-rate from IDS
 * - {IDS_thermal_P_grav_standard} - Standard gravimetric power density from IDS
 * - {maxGravEnergyDensity}, {maxGravPowerDensity} - Global max values for normalization
 * - {maxCapacityVariation}, {maxCapacityLoss} - Global max values for quality scores
 *
 * ──────────────────────────────────────────────────────────────────────────
 *  SINGLE SOURCE OF TRUTH — the 4 measurement conditions
 *
 *  The cell-agnostic condition definitions (C/10, Standard, Continuous,
 *  Peak) now live ONCE in `shared/metric-methodology.json` (the
 *  `condition_*` blocks, stored as parameterized {…} templates) and are
 *  imported below. The backend reads the same blocks (rendering generic
 *  prose for the AI). Edit the condition wording in the shared JSON only
 *  — both this file and the Ion AI update automatically.
 *
 *  Other methodology prose below (DCIR, AC resistance, max temperature,
 *  etc.) still has a sibling key in the shared JSON; mirror those edits
 *  there until they too are imported.
 * ──────────────────────────────────────────────────────────────────────────
 */

import metricMethodology from '../../../../shared/metric-methodology.json'

// Canonical, parameterized condition templates ({I_c10}, {V_min}, …).
// renderDefinition() interpolates the per-cell values exactly as before.
const COND = metricMethodology.blocks
const CONDITION_FIELDS = {
  intro: COND.condition_overview,
  c10: COND.condition_c10,
  standard: COND.condition_standard,
  continuous: COND.condition_continuous,
  peak: COND.condition_peak,
}

// ============================================================================
// MY PLOT / REQUIREMENT PLOT SECTION
// ============================================================================

export const MY_PLOT = {
  title: "My Plot (Personalized Ragone)",

  intro: `In a regular Ragone plot, usually the peak power (measured at some small energy) is plotted versus the maximum available energy (measured at some small power).`,

  concept: `This is meaningful, as the location of a dot shows the edge performance of a cell. But what if we are not interested in the edge performance, but in the performance of cells for the power and energy relevant for us? The cell having a very high energy density: Does it still have that high energy density at the power you require? For that application we can plot a different version of the Ragone diagram:

There, we plot the power (determined at exactly the required energy from the slider) versus the energy (determined at exactly the required power from the slider).

This is your personalized Ragone Plot for exactly your relevant energy and power levels! (The Ragone plot you know is basically a special case of this plot and vice versa, the shown plot is a generalization of the known Ragone plot.)`,

  howToUse: {
    title: "How to use it?",
    items: [
      `Based on the value of the "Required Power" slider it is determined for each cell, how much energy it can deliver at that power. Hence, changing the slider value moves the points up and down. A cell which can not deliver the required power disappears.`,
      `Based on the value of the "Required Energy" slider it is determined for each cell, how much power it can deliver at that energy. Hence, changing the slider value moves the points left and right. A cell which can not deliver the required energy disappears.`,
      `Additionally, both slider values show up as grid lines in the plot, creating areas where cells fulfill one (orange) or both slider requirements (green).`
    ]
  }
}

// ============================================================================
// FACT SHEET SECTION
// ============================================================================

export const FACTSHEET = {
  scores: {
    title: "Scores Info",
    intro: "The radar chart shows four key performance scores compared to average of all cells in Batemo Insights:",

    energy: {
      title: "Energy Score",
      text: `Based on C/10 energy density. Batemo measured the C/10 energy and capacity by discharging the cell at an ambient temperature of 25°C from 100% with a constant current of {I_c10} A (0.1C) until reaching the voltage of {V_min} V. The thermal boundary condition is {thermalBoundary}. The axis of the radar chart is normalized to a range of 0 to {maxGravEnergyDensity} Wh/kg with higher energy density cells being more outward.`
    },

    power: {
      title: "Power Score",
      text: `Based on peak power density. The peak power is the power that the cell can supply for 5 minutes. The cell is discharged from 100% SOC at an ambient temperature of 25°C with a constant current until it reaches either the lower voltage limit of {V_min} V or the maximum surface temperature of {T_max}°C after 5 minutes. For cells that reach the maximum surface temperature, the measured current is taken directly as the peak current. For cells that do not reach the maximum surface temperature after 5 minutes because they reach the lower voltage limit first, the measured current is multiplied by a correction factor that estimates the current that would have heated the cell to the maximum surface temperature within 5 minutes. The thermal boundary condition is {thermalBoundary}. The axis of the radar chart is normalized to a range of 0 to {maxGravPowerDensity} W/kg with higher power density cells being more outward.`
    },

    batchVariation: {
      title: "Cell-to-Cell Variation Score",
      text: `Estimates manufacturing consistency. When cells are measured in the Batemo Lab, they are first pre-cycled for 10 cycles. To estimate the Cell-to-Cell Variation, capacity and impedance measurements of at least 4 cells are compared at the end of pre-cycling. The given score value represents the standard deviation of the capacity measurements on the measured cells. Lower values indicate better consistency. Note, that this number does not give information about the batch-to-batch variation of cells (e.g. between production batches from different facilities). Also, the given number represents the variation in the cells selected for characterization at Batemo. A manufacturer might be able to provide carefully selected cells with lower variability. The axis of the radar chart is normalized to a range of {maxCapacityVariation} to 1 and the location of the score dot is at 1-Score Value, so that lower capacity variations are more outward.`
    },

    initialAging: {
      title: "Post-Formation Loss Score",
      text: `Measures capacity change during the first equivalent full cycles after pre-cycling. As formation is a costly process step, for some cells formation might not be completely finished, so that cells show an initial capacity fade at the begin of operation. In that case, a cell quickly loses some of its capacity and thus after only a few cycles it ends up at a capacity smaller than expected. However, some cells may also show a capacity gain during initial cycling, meaning they actually improved. In order to estimate the severity of this effect, the Post-Formation Loss Score represents the capacity change of a cell during Batemo's validation measurements at an ambient temperature of 25°C during {EFC_initial_aging} equivalent full cycles. Negative values (shown with -) indicate capacity gain (improvement), positive values (shown with +) indicate capacity loss. Lower values indicate better performance. The axis of the radar chart is normalized so that capacity gains are more outward and capacity losses are more inward.`
    }
  },

  mechanical: {
    title: "Mechanical Info",
    text: `Mechanical properties of the battery cell:`,
    items: [
      { label: "Format", description: "Format of the cell" },
      { label: "Size", description: "Physical size (diameter × height or length × width × height)" },
      { label: "Weight", description: "Total cell weight" },
      { label: "Volume", description: "Total volume of the cell" },
      { label: "Surface", description: "Surface area of the cell" }
    ]
  },

  chemical: {
    title: "Chemical Info",
    text: `The active materials of the cell are identified by opening the cell and conducting Energy-dispersive X-ray spectroscopy analysis (EDX). The keyword "blend" indicates that apart from the main material with the largest volume fraction, further active materials with different chemistries and smaller but still significant volume fractions are present. As distinction by EDX is often ambiguous, Aluminum-doped NMC (NMCA) is labelled as NMC. For NMC stoichiometry, the EDX results are rounded to the closest common stoichiometry.`
  },

  quality: {
    title: "Quality Info",

    batchVariation: {
      title: "Cell-to-Cell Variation",
      text: `When cells are measured in the Batemo Lab, they are first pre-cycled for 10 cycles. To estimate the Cell-to-Cell Variation, capacity and impedance measurements of at least 4 cells are compared at the end of pre-cycling. The given score value represents the standard deviation of the capacity measurements on the measured cells. Lower values indicate better consistency. Note, that this number does not give information about the batch-to-batch variation of cells (e.g. between production batches from different facilities). Also, the given number represents the variation in the cells selected for characterization at Batemo. A manufacturer might be able to provide carefully selected cells with lower variability.`
    },

    initialAging: {
      title: "Post-Formation Loss",
      text: `As formation is a costly process step, for some cells formation might not be completely finished, so that cells show an initial capacity fade at the begin of operation. In that case, a cell quickly loses some of its capacity and thus after only a few cycles it ends up at a capacity smaller than expected. However, some cells may also show a capacity gain during initial cycling, meaning they actually improved. In order to estimate the severity of this effect, the Post-Formation Loss represents the capacity change of a cell during Batemo's validation measurements at an ambient temperature of 25°C during {EFC_initial_aging} equivalent full cycles. Negative values (shown with -) indicate capacity gain (improvement), positive values (shown with +) indicate capacity loss. Lower values indicate better performance.`
    }
  },

  electrical: {
    title: "Electrical Info",

    mainConditions: {
      title: "Energy / Power / Capacity / Current",
      // Condition definitions sourced from shared/metric-methodology.json
      // (single source of truth) — see CONDITION_FIELDS above.
      ...CONDITION_FIELDS,
      values: `The given values for power, energy, capacity, current are the respective quantities as derived from the above mentioned experiments. Power and Current are mean values during these experiments, Energy and Capacity are the exchanged energy and load during these experiments.`
    },

    energyDensity: {
      title: "Energy Density / Power Density",
      text: `The energy densities result from the C/10 energy, divided by the cell weight or the cell volume, respectively. The power densities result from the peak power, divided by the cell weight or the cell volume, respectively.`
    },

    nominalCapacity: {
      title: "Nominal Capacity",
      text: `The nominal capacity originates from the manufacturer's data sheet, if available. When the data sheet is unavailable, the nominal capacity is estimated, e.g. from the cell name.`
    },

    voltage: {
      title: "Voltage",
      text: `The minimum and maximum voltage limits are the electrical limits as used in the Batemo battery laboratory. The mean voltage is the mean voltage of a C/10 discharge measurement by discharging the cell at an ambient temperature of 25°C from 100% with a constant current of {I_c10} A (0.1C) until reaching the voltage of {V_min} V. The thermal boundary condition is {thermalBoundary}.`
    },

    resistance: {
      title: "Resistance",
      pulse: `The DCIR (DC Internal Resistance) is defined as the voltage difference just before a discharge current pulse and after applying that current for 10 seconds, divided by the pulse current. Before the pulse, the cell is in rest and at thermal equilibrium at an ambient temperature of 25°C. The default DCIR (that serves as a KPI) is evaluated at an SOC of 80%. The discharge current strength is {IDS_electrical_C_rate_DCP} C. The thermal boundary condition is {thermalBoundary}.`,
      ac: `The AC resistance is defined to be the real part of an impedance spectrum at the intersection of the impedance spectrum with the real axis (often referred to as ohmic resistance of the impedance spectrum). The according frequency is often close to 1 kHz. The AC resistance is thus closely comparable to the commonly stated 1kHz resistance in datasheets. The impedance spectrum was measured at an SOC of 80% and at an ambient temperature of 25°C.`
    },

    // Legacy keys for backward compatibility with FactSheetSection.jsx
    c10Energy: {
      title: "C/10 Energy & Capacity",
      text: `Batemo measured the C/10 energy and capacity by discharging the cell at an ambient temperature of 25°C from 100% with a constant current of {I_c10} A (0.1C) until reaching the voltage of {V_min} V. The thermal boundary condition is {thermalBoundary}.`
    },

    peakPower: {
      title: "Peak Power & Current",
      text: `The peak current is the current that the cell can supply for 5 minutes. The cell is discharged from 100% SOC at an ambient temperature of 25°C with a constant current until it reaches either the lower voltage limit of {V_min} V or the maximum surface temperature of {T_max}°C after 5 minutes. For cells that reach the maximum surface temperature, the measured current is taken directly as the peak current. For cells that do not reach the maximum surface temperature after 5 minutes because they reach the lower voltage limit first, the measured current is multiplied by a correction factor that estimates the current that would have heated the cell to the maximum surface temperature within 5 minutes. The peak power is the mean power related to that current.`
    },

    continuousPower: {
      title: "Continuous Current & Power",
      text: `The continuous current is the highest current that completely discharges the cell without overheating it. The cell is discharged from 100% SOC at 25°C with a constant current until a residual SOC of 10% and either the lower voltage limit of {V_min} V or 90% of the maximum surface temperature {T_max}°C is reached. The thermal boundary condition is {thermalBoundary}.`
    },

    voltageRange: {
      title: "Voltage",
      text: `The minimum and maximum voltage limits are the electrical limits as used in the Batemo battery laboratory. The mean voltage is the mean voltage of a C/10 discharge measurement by discharging the cell at an ambient temperature of 25°C from 100% with a constant current of {I_c10} A (0.1C) until reaching the voltage of {V_min} V. The thermal boundary condition is {thermalBoundary}.`
    },

    pulseResistance: {
      title: "DCIR",
      text: `The DCIR (DC Internal Resistance) is defined as the voltage difference just before a discharge current pulse and after applying that current for 10 seconds, divided by the pulse current. Before the pulse, the cell is in rest and at thermal equilibrium at an ambient temperature of 25°C. The default DCIR (that serves as a KPI) is evaluated at an SOC of 80%. The discharge current strength is {IDS_electrical_C_rate_DCP} C. The thermal boundary condition is {thermalBoundary}.`
    }
  },

  thermal: {
    title: "Thermal Info",

    mainConditions: {
      title: "Heat Energy / Heat Power",
      // Same single-source condition definitions as the electrical card.
      ...CONDITION_FIELDS,
      values: `The given values for Heat Energy and Heat Power are the respective quantities as derived from the above mentioned experiments. The heat power is defined by the heat a cell produces due to internal losses. It is determined by first calculating the experiment's energy W_dc by power integration. The theoretical energy limit without losses W_ocv is obtained by integration of the OCV curve over the same voltage range. The heat energy is then defined to be W_ocv - W_dc. The Heat Power is obtained by dividing Heat Energy by the duration of the experiment.`
    },

    maxTemperature: {
      title: "Maximum Temperature",
      nominal: `The nominal maximum temperature is the maximum temperature as used in the Batemo battery laboratory. When reaching this temperature, all measurements are stopped. The nominal maximum temperature originates from the manufacturer's data sheet, if available.`,
      standard: `The given value for the Standard Maximum Temperature is the maximum reached temperature under the above defined Standard condition. The relevant temperature is the surface temperature of the main sensor (typically mid of the largest surface area of the cell or at the mid of the can, respectively). The Maximum Temperature is usually the temperature at the end of the experiment.`,
      // Legacy key for backward compatibility
      get text() { return this.nominal + " " + this.standard }
    },

    efficiency: {
      title: "Efficiency",
      text: `The given values for efficiency are the respective quantities as derived from the above mentioned experiments. The efficiency is defined as the ratio between usable energy and theoretically available energy. It is determined by first calculating the experiment's energy W_dc by power integration. The theoretical energy limit without losses W_ocv is obtained by integration of the OCV curve. The efficiency is then defined to be W_dc / W_ocv.`
    },

    // Legacy keys for backward compatibility with FactSheetSection.jsx
    heatPower: {
      get title() { return FACTSHEET.thermal.mainConditions.title },
      get text() { return FACTSHEET.thermal.mainConditions.values }
    }
  },

  dischargePlots: {
    title: "Discharge Info",
    text: `The graph shows the result of constant current discharge measurements at an ambient temperature of 25°C. The thermal boundary condition is {thermalBoundary}. The voltage is measured in a 4-point setup at the poles (in order to avoid disturbance by contact resistances). The temperature is the surface temperature of the main sensor (typically mid of the largest surface area of the cell or at the mid of the can, respectively). The measurement stops when reaching either the minimum voltage {V_min} V or a surface temperature higher than the nominal maximum temperature {T_max}°C.`
  }
}

// ============================================================================
// MECHANICAL SECTION
// ============================================================================

export const MECHANICAL = {
  cellDimensions: {
    title: "Cell Dimensions Info",
    text: "Physical cell dimensions:",
    items: [
      { label: "Length/Diameter", description: "Main dimension of the cell" },
      { label: "Width/Radius", description: "Secondary dimension" },
      { label: "Height", description: "Cell thickness or height" },
      { label: "Core", description: "Inner pouch dimensions (pouch cells only)" }
    ]
  },

  poleDimensions: {
    title: "Pole Dimensions Info",
    text: "Pole configuration:",
    items: [
      { label: "Dimensions", description: "Physical size of poles" },
      { label: "Different sizes", description: "Poles can have different dimensions (positive vs negative)" },
      { label: "Location", description: "Position of positive/negative poles" },
      { label: "Same-side", description: "Both poles on same side" },
      { label: "Opposite-side", description: "Poles on opposite sides" }
    ]
  },

  physicalProperties: {
    title: "Physical Properties Info",
    text: "Cell physical characteristics:",
    items: [
      { label: "Mass", description: "Total weight of the cell" },
      { label: "Volume", description: "Total volume of the cell" },
      { label: "Surface Area", description: "Surface area of the cell" }
    ]
  },

  safetyFeatures: {
    title: "Safety Features Info",
    text: "The following safety mechanisms are commonly found in cells:",
    items: [
      { label: "Current Interrupt Device", description: "Internally disconnects the poles from the jelly roll, for example at overpressure: A certain pressure increase inside the cell for changes the position of the CID, causes it to open and thus to interrupt the current path." },
      { label: "PTC Resistor", description: "To control the current flow in case of an external short, sometimes a positive temperature coefficient (PTC) resistor is used, which limits the current once the cell temperature exceeds a certain threshold." },
      { label: "Internal Short Protection", description: "To prevent internal shorting due to dendrite growth or a melted separator, the separator sheets are often coated with ceramic layers. These layers increase the mechanical stability of the separator and stabilize its structure once the polyethylene (PE) or polypropylene (PP) component melts due to a temperature increase above its maximal temperature." },
      { label: "Safety Vent", description: "A safety vent allows the cell to release pressure with the cell. Other than for the Burst Valve, the cell is still being capable to operate after such an event." },
      { label: "Burst Valve", description: "If the pressure inside the cell exceeds a certain safety threshold, it is usually released by a burst valve. Thereby, the cell is prevented from exploding. When the burst valve opens, the cell interior encounters ambient air and humidity after the pressure is released. This causes the cell to take permanent damage." }
    ]
  }
}

// ============================================================================
// CHEMICAL SECTION
// ============================================================================

export const CHEMICAL = {
  chemistry: {
    title: "Chemical Info",
    intro: `The cell's active materials are {anode} (anode) and {cathode} (cathode), used for {chemistry} chemistry.`,
    electrochemistry: `{anode} → {ionLabel} → {cathode}`,
    text: `The active materials of the cell are identified by opening the cell and conducting Energy-dispersive X-ray spectroscopy analysis (EDX). The keyword "blend" indicates that apart from the main material with the largest volume fraction, further active materials with different chemistries and smaller but still significant volume fractions are present. As distinction by EDX is often ambiguous, Aluminum-doped NMC (NMCA) is labelled as NMC. For NMC stoichiometry, the EDX results are rounded to the closest common stoichiometry.`
  },

  materials: {
    title: "Materials Info",
    intro: `{housing} housing provides mechanical protection and electrical isolation for {format} format cells.`,
    text: `The materials of the cell are identified by opening the cell and determining the used metals.`
  }
}

// ============================================================================
// QUALITY SECTION
// ============================================================================

export const QUALITY = {
  batchVariation: {
    title: "Cell-to-Cell Variation Info",
    intro: `When cells are measured in the Batemo Lab, they are first pre-cycled for 10 cycles. To estimate the Cell-to-Cell Variation, capacity and impedance measurements of at least 4 cells are compared at the end of pre-cycling.`,

    capacity: {
      title: "Capacity",
      text: `Batemo measured the capacity by discharging the cell at an ambient temperature of 25°C from 100% with a constant current of 0.5C until reaching the lower voltage limit. The thermal boundary condition is {thermalBoundary}.`
    },

    impedance: {
      title: "Impedance",
      text: `The depicted values for Impedance Spread are defined by the spread of the sum of AC resistance and Polarisation Resistance. The impedance was measured at an SOC of 80% and at an ambient temperature of 25°C. The AC resistance is defined to be the real part of an impedance spectrum at the intersection of the impedance spectrum with the real axis (often referred to as ohmic resistance of the impedance spectrum). The polarization resistance is defined to be the size of the impedance semicircle. It is defined as the difference between the AC resistance and the real part of the impedance at the local minimum of the negative imaginary part of the impedance, just between the polarization part of the impedance spectrum and the capacitive branch. The according frequency is often close to 1 Hz. Note, that these numbers do not give information about the batch-to-batch variation of cells (e.g. between production batches from different facilities). Also, the given numbers represents the variation in the cells selected for characterization at Batemo. A manufacturer might be able to provide carefully selected cells with lower variability.`
    }
  },

  initialAging: {
    title: "Post-Formation Loss Info",
    intro: `As formation is a costly process step, for some cells formation might not be completely finished, so that cells show an initial capacity fade or impedance growth at the begin of operation. In order to estimate the severity of this effect, the capacity loss and impedance growth of a cell during {EFC_initial_aging} equivalent full cycles of Batemo's validation measurements at an ambient temperature of 25°C is displayed.`,

    capacityFade: {
      title: "Capacity",
      text: `Batemo measured the capacity by discharging the cell at an ambient temperature of 25°C from 100% with a constant current of 0.5C until reaching the lower voltage limit. The thermal boundary condition is {thermalBoundary}.`
    },

    impedanceGrowth: {
      title: "Impedance",
      text: `The depicted values for Impedance Growth are defined by the spread of the sum of AC resistance and Polarisation Resistance. The impedance was measured at an SOC of 80% and at an ambient temperature of 25°C. The AC resistance is defined to be the real part of an impedance spectrum at the intersection of the impedance spectrum with the real axis (often referred to as ohmic resistance of the impedance spectrum). The polarization resistance is defined to be the size of the impedance semicircle. It is defined as the difference between the AC resistance and the real part of the impedance at the local minimum of the negative imaginary part of the impedance, just between the polarization part of the impedance spectrum and the capacitive branch. The according frequency is often close to 1 Hz. A positive value for capacity loss is connected to a decreasing capacity, a positive value for impedance growth with an increasing impedance.`
    }
  }
}

// ============================================================================
// ELECTRICAL SECTION - Flipcard Definitions
// ============================================================================

// Shared text for Energy and Power flipcards (mainConditions)
const ELECTRICAL_MAIN_CONDITIONS = `<p><strong>Energy / Power / Capacity / Current</strong><br/>
Batemo distinguishes between 4 main measurement conditions, from which different KPI of the cell are derived. For all conditions the thermal boundary condition is {thermalBoundary}. The conditions are as follows:</p>

<p><strong>C/10:</strong> The C/10 condition is defined as a constant current discharge with a current of C/10. The cell is discharged from 100% SOC at an ambient temperature of 25°C with a constant current of {I_c10} A (0.1C) until it reaches the lower voltage limit of {V_min} V.</p>

<p><strong>Standard:</strong> The standard condition is defined as a constant current discharge with an average gravimetric power density of {IDS_thermal_P_grav_standard} Wh/kg. This value is the same for all cells and it thus allows to compare the behaviour of cells at that same gravimetric power density value. The cell is discharged from 100% state of charge (SOC) at an ambient temperature of 25°C with a constant current that leads to a mean discharge power equivalent to the standard gravimetric power density. These operating conditions may be outside the cell manufacturer's specification.</p>

<p><strong>Continuous:</strong> The continuous condition is defined as a constant current discharge with the highest current that completely discharges the cell without overheating it. The cell is discharged from 100% SOC at 25°C with a constant current until a residual SOC of 10% and either the lower voltage limit of {V_min} V or 90% of the maximum surface temperature {T_max}°C is reached.</p>

<p><strong>Peak:</strong> The peak condition is defined as a constant current discharge with the maximum current that the cell can supply for 5 minutes. The cell is discharged from 100% SOC at an ambient temperature of 25°C with a constant current until it reaches either the lower voltage limit of {V_min} V or the maximum surface temperature of {T_max}°C after 5 minutes. For cells that reach the maximum surface temperature, the measured current is taken directly as the peak current. For cells that do not reach the maximum surface temperature after 5 minutes because they reach the lower voltage limit first, the measured current is multiplied by a correction factor that estimates the current that would have heated the cell to the maximum surface temperature within 5 minutes. The peak power is the mean power related to that current.</p>`

export const ELECTRICAL_SECTION = {
  energy: {
    // Dynamic title based on domain (power vs current)
    getTitle: (domain) => domain === 'energy-power'
      ? "How much energy can the cell deliver at a given power?"
      : "How much energy can the cell deliver at a given current?",
    title: "How much energy can the cell deliver at a given power?", // Legacy fallback
    text: ELECTRICAL_MAIN_CONDITIONS
  },

  power: {
    // Dynamic title based on domain (power vs current)
    getTitle: (domain) => domain === 'energy-power'
      ? "How long can the cell sustain a given power?"
      : "How long can the cell sustain a given current?",
    title: "How long can the cell sustain a given power?", // Legacy fallback
    text: ELECTRICAL_MAIN_CONDITIONS
  },

  voltage: {
    title: "How does voltage change with state of charge?",
    text: `<p><strong>Voltage</strong><br/>
The minimum and maximum voltage limits are the electrical limits as used in the Batemo battery laboratory.</p>

<p>The mean voltage is the mean voltage of a C/10 discharge measurement by discharging the cell at an ambient temperature of 25°C from 100% with a constant current of {I_c10} A (0.1C) until reaching the voltage of {V_min} V. The thermal boundary condition is {thermalBoundary}.</p>`
  },

  acImpedance: {
    title: "What is the AC impedance behavior?",
    text: `<p><strong>Impedance</strong></p>

<p>The AC resistance is defined to be the real part of an impedance spectrum at the intersection of the impedance spectrum with the real axis (often referred to as ohmic resistance of the impedance spectrum). The according frequency is often close to 1 kHz. The AC resistance is thus closely comparable to the commonly stated 1kHz resistance in datasheets. The impedance spectrum was measured at an SOC of 80% and at an ambient temperature of 25°C.</p>

<p>The polarization resistance is defined to be the size of the impedance semicircle. It is defined as the difference between the AC resistance and the real part of the impedance at the local minimum of the negative imaginary part of the impedance, just between the polarization part of the impedance spectrum and the capacitive branch. The according frequency is often close to 1 Hz. If the polarization part can not be clearly distinguished (e.g. no semicircle is visible), then the real part of the impedance spectrum at 1 Hz is used.</p>`
  },

  dcResistance: {
    title: "What is the DCIR?",
    text: `<p><strong>DCIR</strong></p>

<p>The DCIR (DC Internal Resistance) is defined as the voltage difference just before a discharge current pulse and after applying that current for 10 seconds, divided by the pulse current. Before the pulse, the cell is in rest and at thermal equilibrium at an ambient temperature of 25°C. The default DCIR (that serves as a KPI) is evaluated at an SOC of 80%. The discharge current strength is {IDS_electrical_C_rate_DCP} C. The thermal boundary condition is {thermalBoundary}.</p>`
  }
}

// Legacy export for backward compatibility
export const ELECTRICAL_BANNERS = {
  energy: ELECTRICAL_SECTION.energy.text,
  power: ELECTRICAL_SECTION.power.text,
  voltage: ELECTRICAL_SECTION.voltage.text,
  acImpedance: ELECTRICAL_SECTION.acImpedance.text,
  dcResistance: ELECTRICAL_SECTION.dcResistance.text
}

// ============================================================================
// THERMAL SECTION - Info Banners
// ============================================================================

// ============================================================================
// THERMAL SECTION - Flipcard Definitions
// ============================================================================

// Shared text for Heat Power and Heat Energy flipcards (mainConditions)
const THERMAL_MAIN_CONDITIONS = `<p><strong>Heat Energy / Heat Power</strong><br/>
Batemo distinguishes between 4 main measurement conditions, from which different KPI of the cell are derived. For all conditions the thermal boundary condition is {thermalBoundary}. The conditions are as follows:</p>

<p><strong>C/10:</strong> The C/10 condition is defined as a constant current discharge with a current of C/10. The cell is discharged from 100% SOC at an ambient temperature of 25°C with a constant current of {I_c10} A (0.1C) until it reaches the lower voltage limit of {V_min} V.</p>

<p><strong>Standard:</strong> The standard condition is defined as a constant current discharge with an average gravimetric power density of {IDS_thermal_P_grav_standard} Wh/kg. This value is the same for all cells and it thus allows to compare the behaviour of cells at that same gravimetric power density value. The cell is discharged from 100% state of charge (SOC) at an ambient temperature of 25°C with a constant current that leads to a mean discharge power equivalent to the standard gravimetric power density. These operating conditions may be outside the cell manufacturer's specification.</p>

<p><strong>Continuous:</strong> The continuous condition is defined as a constant current discharge with the highest current that completely discharges the cell without overheating it. The cell is discharged from 100% SOC at 25°C with a constant current until a residual SOC of 10% and either the lower voltage limit of {V_min} V or 90% of the maximum surface temperature {T_max}°C is reached.</p>

<p><strong>Peak:</strong> The peak condition is defined as a constant current discharge with the maximum current that the cell can supply for 5 minutes. The cell is discharged from 100% SOC at an ambient temperature of 25°C with a constant current until it reaches either the lower voltage limit of {V_min} V or the maximum surface temperature of {T_max}°C after 5 minutes. For cells that reach the maximum surface temperature, the measured current is taken directly as the peak current. For cells that do not reach the maximum surface temperature after 5 minutes because they reach the lower voltage limit first, the measured current is multiplied by a correction factor that estimates the current that would have heated the cell to the maximum surface temperature within 5 minutes. The peak power is the mean power related to that current.</p>

<p>The given values for Heat Energy and Heat Power are the respective quantities as derived from the above mentioned experiments. The heat power is defined by the heat a cell produces due to internal losses. It is determined by first calculating the experiment's energy W<sub>dc</sub> by power integration. The theoretical energy limit without losses W<sub>ocv</sub> is obtained by integration of the OCV curve over the same voltage range. The heat energy is then defined to be W<sub>ocv</sub> - W<sub>dc</sub>. The Heat Power is obtained by dividing Heat Energy by the duration of the experiment.</p>`

export const THERMAL_SECTION = {
  heatPower: {
    // Dynamic title based on domain (power vs current)
    getTitle: (domain) => domain === 'energy-power'
      ? "How much heat power is generated at a given power?"
      : "How much heat power is generated at a given current?",
    title: "How much heat power is generated at a given power?", // Legacy fallback
    text: THERMAL_MAIN_CONDITIONS
  },

  heatEnergy: {
    // Dynamic title based on domain (power vs current)
    getTitle: (domain) => domain === 'energy-power'
      ? "How much heat energy is generated at a given power?"
      : "How much heat energy is generated at a given current?",
    title: "How much heat energy is generated at a given power?", // Legacy fallback
    text: THERMAL_MAIN_CONDITIONS
  },

  temperature: {
    // Dynamic title based on domain (power vs current)
    getTitle: (domain) => domain === 'energy-power'
      ? "How hot does the cell get at a given power?"
      : "How hot does the cell get at a given current?",
    title: "How hot does the cell get at a given power?", // Legacy fallback
    text: `<p><strong>Maximum Temperature</strong></p>

<p>The nominal maximum temperature is the maximum temperature as used in the Batemo battery laboratory. When reaching this temperature, all measurements are stopped. The nominal maximum temperature originates from the manufacturer's data sheet, if available.</p>

<p>The given value for the Standard Maximum Temperature is the maximum reached temperature under the above defined Standard condition. The relevant temperature is the surface temperature of the main sensor (typically mid of the largest surface area of the cell of at the mid of the can, respectively). The Maximum Temperature is usually the temperature at the end of the experiment.</p>`
  },

  efficiency: {
    // Dynamic title based on domain (power vs current)
    getTitle: (domain) => domain === 'energy-power'
      ? "How efficient is the cell at a given power?"
      : "How efficient is the cell at a given current?",
    title: "How efficient is the cell at a given power?", // Legacy fallback
    text: `<p><strong>Efficiency</strong></p>

<p>The given values for efficiency are the respective quantities as derived from the above mentioned experiments. The efficiency is defined as the ratio between usable energy and theoretically available energy.</p>

<p>It is determined by first calculating the experiment's energy W<sub>dc</sub> by power integration. The theoretical energy limit without losses W<sub>ocv</sub> is obtained by integration of the OCV curve. The efficiency is then defined to be W<sub>dc</sub> / W<sub>ocv</sub>.</p>`
  }
}

// Legacy export for backward compatibility
export const THERMAL_BANNERS = {
  heatPower: THERMAL_SECTION.heatPower.text,
  heatEnergy: THERMAL_SECTION.heatEnergy.text,
  heatPowerEnergy: THERMAL_SECTION.heatPower.text,
  temperature: THERMAL_SECTION.temperature.text,
  efficiency: THERMAL_SECTION.efficiency.text
}

// ============================================================================
// COMPARE-SPECIFIC DEFINITIONS (no cell-specific values)
// ============================================================================
// These definitions are for the Compare page where multiple cells are shown.
// They use generic language instead of cell-specific placeholders like {I_c10}, {V_min}, etc.

// Generic electrical conditions text for compare views
const COMPARE_ELECTRICAL_CONDITIONS = `<p><strong>Energy / Power / Capacity / Current</strong><br/>
Batemo distinguishes between 4 main measurement conditions, from which different KPI of each cell are derived. For all conditions the thermal boundary condition depends on the cell (braced or free convection). The conditions are as follows:</p>

<p><strong>C/10:</strong> The C/10 condition is defined as a constant current discharge with a current of C/10. The cell is discharged from 100% SOC at an ambient temperature of 25°C with a constant current of 0.1C until it reaches the lower voltage limit.</p>

<p><strong>Standard:</strong> The standard condition is defined as a constant current discharge with an average gravimetric power density. This value is the same for all cells and it thus allows to compare the behaviour of cells at that same gravimetric power density value. The cell is discharged from 100% state of charge (SOC) at an ambient temperature of 25°C with a constant current that leads to a mean discharge power equivalent to the standard gravimetric power density. These operating conditions may be outside the cell manufacturer's specification.</p>

<p><strong>Continuous:</strong> The continuous condition is defined as a constant current discharge with the highest current that completely discharges the cell without overheating it. The cell is discharged from 100% SOC at 25°C with a constant current until a residual SOC of 10% and either the lower voltage limit or 90% of the maximum surface temperature is reached.</p>

<p><strong>Peak:</strong> The peak condition is defined as a constant current discharge with the maximum current that the cell can supply for 5 minutes. The cell is discharged from 100% SOC at an ambient temperature of 25°C with a constant current until it reaches either the lower voltage limit or the maximum surface temperature after 5 minutes. For cells that reach the maximum surface temperature, the measured current is taken directly as the peak current. For cells that do not reach the maximum surface temperature after 5 minutes because they reach the lower voltage limit first, the measured current is multiplied by a correction factor that estimates the current that would have heated the cell to the maximum surface temperature within 5 minutes. The peak power is the mean power related to that current.</p>`

export const COMPARE_ELECTRICAL_SECTION = {
  energy: {
    getTitle: (domain) => domain === 'energy-power'
      ? "How much energy can the cells deliver at a given power?"
      : "How much energy can the cells deliver at a given current?",
    title: "How much energy can the cells deliver at a given power?",
    text: COMPARE_ELECTRICAL_CONDITIONS
  },

  power: {
    getTitle: (domain) => domain === 'energy-power'
      ? "How long can the cells sustain a given power?"
      : "How long can the cells sustain a given current?",
    title: "How long can the cells sustain a given power?",
    text: COMPARE_ELECTRICAL_CONDITIONS
  },

  voltage: {
    title: "How does voltage change with state of charge?",
    text: `<p><strong>Voltage</strong><br/>
The minimum and maximum voltage limits are the electrical limits as used in the Batemo battery laboratory.</p>

<p>The mean voltage is the mean voltage of a C/10 discharge measurement by discharging the cell at an ambient temperature of 25°C from 100% with a constant current of 0.1C until reaching the lower voltage limit. The thermal boundary condition depends on the cell (braced or free convection).</p>`
  },

  acImpedance: {
    title: "What is the AC impedance behavior?",
    text: `<p><strong>Impedance</strong></p>

<p>The AC resistance is defined to be the real part of an impedance spectrum at the intersection of the impedance spectrum with the real axis (often referred to as ohmic resistance of the impedance spectrum). The according frequency is often close to 1 kHz. The AC resistance is thus closely comparable to the commonly stated 1kHz resistance in datasheets. The impedance spectrum was measured at an SOC of 80% and at an ambient temperature of 25°C.</p>

<p>The polarization resistance is defined to be the size of the impedance semicircle. It is defined as the difference between the AC resistance and the real part of the impedance at the local minimum of the negative imaginary part of the impedance, just between the polarization part of the impedance spectrum and the capacitive branch. The according frequency is often close to 1 Hz. If the polarization part can not be clearly distinguished (e.g. no semicircle is visible), then the real part of the impedance spectrum at 1 Hz is used.</p>`
  },

  dcResistance: {
    title: "What is the DCIR?",
    text: `<p><strong>DCIR</strong></p>

<p>The DCIR (DC Internal Resistance) is defined as the voltage difference just before a discharge current pulse and after applying that current for 10 seconds, divided by the pulse current. Before the pulse, the cell is in rest and at thermal equilibrium at an ambient temperature of 25°C. The default DCIR (that serves as a KPI) is evaluated at an SOC of 80%. The thermal boundary condition depends on the cell (braced or free convection).</p>`
  }
}

// Generic thermal conditions text for compare views
const COMPARE_THERMAL_CONDITIONS = `<p><strong>Heat Energy / Heat Power</strong><br/>
Batemo distinguishes between 4 main measurement conditions, from which different KPI of each cell are derived. For all conditions the thermal boundary condition depends on the cell (braced or free convection). The conditions are as follows:</p>

<p><strong>C/10:</strong> The C/10 condition is defined as a constant current discharge with a current of C/10. The cell is discharged from 100% SOC at an ambient temperature of 25°C with a constant current of 0.1C until it reaches the lower voltage limit.</p>

<p><strong>Standard:</strong> The standard condition is defined as a constant current discharge with an average gravimetric power density. This value is the same for all cells and it thus allows to compare the behaviour of cells at that same gravimetric power density value. The cell is discharged from 100% state of charge (SOC) at an ambient temperature of 25°C with a constant current that leads to a mean discharge power equivalent to the standard gravimetric power density. These operating conditions may be outside the cell manufacturer's specification.</p>

<p><strong>Continuous:</strong> The continuous condition is defined as a constant current discharge with the highest current that completely discharges the cell without overheating it. The cell is discharged from 100% SOC at 25°C with a constant current until a residual SOC of 10% and either the lower voltage limit or 90% of the maximum surface temperature is reached.</p>

<p><strong>Peak:</strong> The peak condition is defined as a constant current discharge with the maximum current that the cell can supply for 5 minutes. The cell is discharged from 100% SOC at an ambient temperature of 25°C with a constant current until it reaches either the lower voltage limit or the maximum surface temperature after 5 minutes. For cells that reach the maximum surface temperature, the measured current is taken directly as the peak current. For cells that do not reach the maximum surface temperature after 5 minutes because they reach the lower voltage limit first, the measured current is multiplied by a correction factor that estimates the current that would have heated the cell to the maximum surface temperature within 5 minutes. The peak power is the mean power related to that current.</p>

<p>The given values for Heat Energy and Heat Power are the respective quantities as derived from the above mentioned experiments. The heat power is defined by the heat a cell produces due to internal losses. It is determined by first calculating the experiment's energy W<sub>dc</sub> by power integration. The theoretical energy limit without losses W<sub>ocv</sub> is obtained by integration of the OCV curve over the same voltage range. The heat energy is then defined to be W<sub>ocv</sub> - W<sub>dc</sub>. The Heat Power is obtained by dividing Heat Energy by the duration of the experiment.</p>`

export const COMPARE_THERMAL_SECTION = {
  heatPower: {
    getTitle: (domain) => domain === 'energy-power'
      ? "How much heat power is generated at a given power?"
      : "How much heat power is generated at a given current?",
    title: "How much heat power is generated at a given power?",
    text: COMPARE_THERMAL_CONDITIONS
  },

  heatEnergy: {
    getTitle: (domain) => domain === 'energy-power'
      ? "How much heat energy is generated at a given power?"
      : "How much heat energy is generated at a given current?",
    title: "How much heat energy is generated at a given power?",
    text: COMPARE_THERMAL_CONDITIONS
  },

  temperature: {
    getTitle: (domain) => domain === 'energy-power'
      ? "How hot do the cells get at a given power?"
      : "How hot do the cells get at a given current?",
    title: "How hot do the cells get at a given power?",
    text: `<p><strong>Maximum Temperature</strong></p>

<p>The nominal maximum temperature is the maximum temperature as used in the Batemo battery laboratory. When reaching this temperature, all measurements are stopped. The nominal maximum temperature originates from the manufacturer's data sheet, if available.</p>

<p>The given value for the Standard Maximum Temperature is the maximum reached temperature under the above defined Standard condition. The relevant temperature is the surface temperature of the main sensor (typically mid of the largest surface area of the cell or at the mid of the can, respectively). The Maximum Temperature is usually the temperature at the end of the experiment.</p>`
  },

  efficiency: {
    getTitle: (domain) => domain === 'energy-power'
      ? "How efficient are the cells at a given power?"
      : "How efficient are the cells at a given current?",
    title: "How efficient are the cells at a given power?",
    text: `<p><strong>Efficiency</strong></p>

<p>The given values for efficiency are the respective quantities as derived from the above mentioned experiments. The efficiency is defined as the ratio between usable energy and theoretically available energy.</p>

<p>It is determined by first calculating the experiment's energy W<sub>dc</sub> by power integration. The theoretical energy limit without losses W<sub>ocv</sub> is obtained by integration of the OCV curve. The efficiency is then defined to be W<sub>dc</sub> / W<sub>ocv</sub>.</p>`
  }
}

// Compare-specific mechanical definitions (no cell-specific values)
export const COMPARE_MECHANICAL = {
  dimensions: {
    title: "Cell Dimensions",
    text: `<p><strong>Cell Dimensions</strong></p>
<p>Physical cell dimensions depend on the cell format:</p>
<ul>
<li><strong>Cylindrical cells:</strong> Diameter × Height</li>
<li><strong>Prismatic/Pouch cells:</strong> Length × Width × Height (or Thickness)</li>
</ul>
<p>Dimensions are measured from the cell housing exterior. For pouch cells, an inner core dimension may also be provided.</p>`
  },

  poleLocations: {
    title: "Pole/Tab Dimensions",
    text: `<p><strong>Pole Configuration</strong></p>
<p>The poles (or tabs for pouch cells) connect the cell to the external circuit. Key characteristics include:</p>
<ul>
<li><strong>Location:</strong> Same-side or opposite-side configuration</li>
<li><strong>Dimensions:</strong> Physical size of positive and negative terminals</li>
<li><strong>Polarity marking:</strong> Identification of positive (+) and negative (-) terminals</li>
</ul>`
  },

  physicalProperties: {
    title: "Physical Properties",
    text: `<p><strong>Physical Properties</strong></p>
<ul>
<li><strong>Mass:</strong> Total weight of the cell in grams</li>
<li><strong>Volume:</strong> Total volume of the cell in cm³</li>
<li><strong>Surface Area:</strong> Total surface area of the cell in cm²</li>
</ul>
<p>These values are used to calculate gravimetric (per-mass) and volumetric (per-volume) energy and power densities.</p>`
  },

  safetyFeatures: {
    title: "Safety Features",
    text: `<p><strong>Safety Features</strong></p>
<p>Common safety mechanisms in lithium-ion cells:</p>
<ul>
<li><strong>Current Interrupt Device (CID):</strong> Disconnects the cell at overpressure</li>
<li><strong>PTC Resistor:</strong> Limits current at elevated temperatures</li>
<li><strong>Ceramic Separator Coating:</strong> Prevents internal shorting</li>
<li><strong>Safety Vent:</strong> Controlled pressure release</li>
<li><strong>Burst Valve:</strong> Emergency pressure release at critical levels</li>
</ul>`
  }
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Replace placeholders in a definition text with actual values
 * @param {string} text - Text with {placeholder} syntax
 * @param {object} data - Object with values to replace
 * @returns {string} Text with placeholders replaced
 *
 * @example
 * replacePlaceholders("Voltage limit is {V_min} V", { V_min: "2.5" })
 * // Returns: "Voltage limit is 2.5 V"
 */
export function replacePlaceholders(text, data = {}) {
  if (!text) return ''

  return text.replace(/\{(\w+)\}/g, (match, key) => {
    if (data[key] !== undefined && data[key] !== null) {
      return data[key]
    }
    return match // Keep placeholder if no value provided
  })
}

/**
 * Get thermal boundary condition text
 * @param {object} cellData - Cell data object
 * @returns {string} "braced" or "free convection"
 */
export function getThermalBoundary(cellData) {
  return cellData?.mechanical?.is_braced ? 'braced' : 'free convection'
}

/**
 * Build data object for placeholder replacement from cellData
 * @param {object} cellData - Full cell data object
 * @param {object} globalStats - Optional global statistics for normalization ranges
 * @returns {object} Flattened data for placeholder replacement
 */
export function buildPlaceholderData(cellData, globalStats = {}) {
  if (!cellData) return {}

  const elec = cellData.electrical || {}
  const mech = cellData.mechanical || {}
  const chem = cellData.chemical || {}
  const aging = cellData.aging || {}
  const thermal = cellData.thermal || {}
  const technology = cellData.technology || 'Li-Ion'

  return {
    // Chemical
    anode: chem.anode || 'N/A',
    cathode: chem.cathode || 'N/A',
    chemistry: chem.main_chemistry || 'N/A',
    ionLabel: technology?.includes('Na') ? 'Na⁺' : 'Li⁺',
    housing: chem.housing || 'N/A',

    // Mechanical
    format: mech.format || 'N/A',
    thermalBoundary: mech.is_braced ? 'braced' : 'free convection',

    // Electrical - I_c10 is in Ampere (A), not Ah!
    I_c10: elec.I_c10_A != null ? elec.I_c10_A.toFixed(2) : 'N/A',
    V_min: elec.V_min != null ? elec.V_min.toFixed(2) : 'N/A',
    V_max: elec.V_max != null ? elec.V_max.toFixed(2) : 'N/A',

    // Thermal - T_max from thermal.T_max_peak (peak discharge test limit) or fallback to 80°C
    T_max: thermal.T_max_peak != null ? Math.round(thermal.T_max_peak) :
           (thermal.T_max_dc_25 != null ? Math.round(thermal.T_max_dc_25) : '80'),

    // Aging
    EFC_initial_aging: aging.EFC_initial_aging != null ? aging.EFC_initial_aging : 'N/A',

    // IDS-like values - directly from electrical/thermal (not nested IDS object!)
    IDS_electrical_C_rate_DCP: elec.C_rate_DCP != null ? elec.C_rate_DCP.toFixed(1) : 'N/A',
    IDS_thermal_P_grav_standard: thermal.P_grav_standard != null ? thermal.P_grav_standard.toFixed(0) : 'N/A',

    // Global statistics for radar chart axis ranges
    maxGravEnergyDensity: globalStats.maxGravEnergyDensity != null ? globalStats.maxGravEnergyDensity.toFixed(0) : 'max',
    maxGravPowerDensity: globalStats.maxGravPowerDensity != null ? globalStats.maxGravPowerDensity.toFixed(0) : 'max',
    maxCapacityVariation: globalStats.maxCapacityVariation != null ? (1 - globalStats.maxCapacityVariation).toFixed(2) : '0',
    maxCapacityLoss: globalStats.maxCapacityLoss != null ? (1 - globalStats.maxCapacityLoss).toFixed(2) : '0'
  }
}

/**
 * Render a definition with placeholders replaced
 * @param {string} text - Definition text with placeholders
 * @param {object} cellData - Full cell data object
 * @param {object} globalStats - Optional global statistics for normalization ranges
 * @returns {string} Rendered definition
 */
export function renderDefinition(text, cellData, globalStats = {}) {
  const data = buildPlaceholderData(cellData, globalStats)
  return replacePlaceholders(text, data)
}

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

export default {
  FACTSHEET,
  MECHANICAL,
  CHEMICAL,
  QUALITY,
  ELECTRICAL_SECTION,
  ELECTRICAL_BANNERS,
  THERMAL_SECTION,
  THERMAL_BANNERS,
  COMPARE_ELECTRICAL_SECTION,
  COMPARE_THERMAL_SECTION,
  COMPARE_MECHANICAL,  MY_PLOT,
  replacePlaceholders,
  getThermalBoundary,
  buildPlaceholderData,
  renderDefinition
}
